'use client'

import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useState,
} from 'react'
import { FormElementInstance } from '../FormElements'
import { DEFAULT_BOUNCE_BUTTON, type BounceButtonConfig } from '@/lib/bounce-button'
import { DEFAULT_FLOATING_MENU_STYLE, type FloatingMenuStyle } from '@/lib/floating-menu'
import type { GuidedFlowConfig } from '@/lib/guided-flow'
import type { ResultPageConfig } from '@/lib/result-page'

export type InsertTarget =
    | { kind: 'canvas' }
    | { kind: 'tab'; parentId: string; tabId: string }
    | { kind: 'layout'; parentId: string; slotId: string }
    | { kind: 'repeatable'; parentId: string }

type DesignerContextType = {
  elements: FormElementInstance[]
  setElements: Dispatch<SetStateAction<FormElementInstance[]>>
  addElement: (index: number, element: FormElementInstance) => void
  removeElement: (id: string) => void
  customCss: string
  setCustomCss: Dispatch<SetStateAction<string>>

  /** Form-level: navigate sections via a draggable floating menu instead of the dropdown/tab strip. */
  useFloatingMenu: boolean
  setUseFloatingMenu: Dispatch<SetStateAction<boolean>>

  /** Form-level: that menu's spawn corner, colour, icon and panel height (lib/floating-menu.ts). */
  floatingMenuStyle: FloatingMenuStyle
  setFloatingMenuStyle: Dispatch<SetStateAction<FloatingMenuStyle>>

  /** Form-level: the bounce button — whether it shows, what it jumps to, how it looks (lib/bounce-button.ts). */
  bounceButton: BounceButtonConfig
  setBounceButton: Dispatch<SetStateAction<BounceButtonConfig>>

  /** Form-level: tutorial mode — a curated step order the live form can walk (lib/guided-flow.ts). */
  guidedFlow: GuidedFlowConfig | undefined
  setGuidedFlow: Dispatch<SetStateAction<GuidedFlowConfig | undefined>>

  /** Form-level: post-submission success/failure card copy (lib/result-page.ts). */
  resultPage: ResultPageConfig | undefined
  setResultPage: Dispatch<SetStateAction<ResultPageConfig | undefined>>

  /** Form-level: library action folders the field ⋮ menus draw their pre-canned actions from. */
  actionFolderIds: string[]
  setActionFolderIds: Dispatch<SetStateAction<string[]>>

  selectedElement: FormElementInstance | null
  setSelectedElement: Dispatch<SetStateAction<FormElementInstance | null>>

  updateElement: (id: string, element: FormElementInstance) => void
  updateNestedLayoutSlot: (
    layoutId: string,
    slotId: string,
    mutator: (children: FormElementInstance[]) => FormElementInstance[]
  ) => void

  // ✅ NEW: where sidebar-click inserts into
  insertTarget: InsertTarget
  setInsertTarget: Dispatch<SetStateAction<InsertTarget>>

  /** Right-side panel beside canvas: Word template placeholders (form builder). */
  linkedDocTemplatesPanelOpen: boolean
  setLinkedDocTemplatesPanelOpen: Dispatch<SetStateAction<boolean>>
}

export const DesignerContext = createContext<DesignerContextType | null>(null)

export default function DesignerContextProvider({
                                                  children,
                                                }: {
  children: ReactNode
}) {
  const [elements, setElements] = useState<FormElementInstance[]>([])
  const [selectedElement, setSelectedElement] =
      useState<FormElementInstance | null>(null)
  const [customCss, setCustomCss] = useState<string>("")
  const [useFloatingMenu, setUseFloatingMenu] = useState<boolean>(false)
  const [floatingMenuStyle, setFloatingMenuStyle] = useState<FloatingMenuStyle>(
    DEFAULT_FLOATING_MENU_STYLE
  )
  const [bounceButton, setBounceButton] = useState<BounceButtonConfig>(DEFAULT_BOUNCE_BUTTON)
  const [guidedFlow, setGuidedFlow] = useState<GuidedFlowConfig | undefined>(undefined)
  const [resultPage, setResultPage] = useState<ResultPageConfig | undefined>(undefined)
  const [actionFolderIds, setActionFolderIds] = useState<string[]>([])

  // ✅ NEW: default to canvas
  const [insertTarget, setInsertTarget] = useState<InsertTarget>({
    kind: 'canvas',
  })

  const [linkedDocTemplatesPanelOpen, setLinkedDocTemplatesPanelOpen] = useState(false)

  const addElement = (index: number, element: FormElementInstance) => {
    setElements((prev) => {
      const newElements = [...prev]
      newElements.splice(index, 0, element)
      return newElements
    })
  }

  const removeElement = (id: string) => {
    setElements((prev) => prev.filter((element) => element.id !== id))
  }

  const updateElement = (id: string, element: FormElementInstance) => {
    setElements((prev) => {
      const newElements = [...prev]
      const index = newElements.findIndex((el) => el.id === id)

      // If found in top-level elements, update it
      if (index !== -1) {
        newElements[index] = element
        return newElements
      }

      // Otherwise, check if it's a child of a TabsField
      for (let i = 0; i < newElements.length; i++) {
        const el = newElements[i]
        if (el.type === 'TabsField' && el.extraAttributes?.tabs) {
          const tabs = el.extraAttributes.tabs as {
            id: string
            label: string
            children: FormElementInstance[]
          }[]

          // Check each tab for the child element
          let found = false
          const updatedTabs = tabs.map((tab) => {
            const childIndex = tab.children?.findIndex((child) => child.id === id)
            if (childIndex !== undefined && childIndex !== -1) {
              found = true
              const updatedChildren = [...(tab.children || [])]
              updatedChildren[childIndex] = element
              return { ...tab, children: updatedChildren }
            }
            return tab
          })

          if (found) {
            // Update the parent TabsField with the updated tabs
            newElements[i] = {
              ...el,
              extraAttributes: {
                ...el.extraAttributes,
                tabs: updatedTabs,
              },
            }
            return newElements
          }
        }

        if (el.type === 'RepeatableField' && el.extraAttributes?.schema) {
          const schema = el.extraAttributes.schema as FormElementInstance[]
          const childIndex = schema.findIndex((child) => child.id === id)
          if (childIndex !== -1) {
            const updatedSchema = [...schema]
            updatedSchema[childIndex] = element
            newElements[i] = {
              ...el,
              extraAttributes: { ...el.extraAttributes, schema: updatedSchema },
            }
            return newElements
          }
        }

        if (el.type === 'LayoutField' && el.extraAttributes?.slots) {
          const slots = el.extraAttributes.slots as {
            id: string
            label: string
            children: FormElementInstance[]
          }[]

          let foundLayout = false
          const updatedSlots = slots.map((slot) => {
            const childIndex = slot.children?.findIndex((child) => child.id === id)
            if (childIndex !== undefined && childIndex !== -1) {
              foundLayout = true
              const updatedChildren = [...(slot.children || [])]
              updatedChildren[childIndex] = element
              return { ...slot, children: updatedChildren }
            }
            return slot
          })

          if (foundLayout) {
            newElements[i] = {
              ...el,
              extraAttributes: {
                ...el.extraAttributes,
                slots: updatedSlots,
              },
            }
            return newElements
          }
        }
      }

      // If not found anywhere, return unchanged
      return newElements
    })

    // Update selectedElement if it's the same element being updated
    if (selectedElement?.id === id) {
      setSelectedElement(element)
    }
  }

  const updateNestedLayoutSlot = (
    layoutId: string,
    slotId: string,
    mutator: (children: FormElementInstance[]) => FormElementInstance[]
  ) => {
    setElements((prev) => {
      const newElements = [...prev]

      for (let i = 0; i < newElements.length; i++) {
        const el = newElements[i]

        if (el.id === layoutId && el.type === 'LayoutField') {
          const slots = (el.extraAttributes?.slots ?? []) as { id: string; label: string; children: FormElementInstance[] }[]
          const si = slots.findIndex((s) => s.id === slotId)
          if (si === -1) return prev
          const nextSlots = [...slots]
          nextSlots[si] = { ...slots[si], children: mutator(slots[si].children ?? []) }
          newElements[i] = { ...el, extraAttributes: { ...el.extraAttributes, slots: nextSlots } }
          return newElements
        }

        if (el.type === 'TabsField' && el.extraAttributes?.tabs) {
          const tabs = el.extraAttributes.tabs as { id: string; label: string; children: FormElementInstance[] }[]
          for (let ti = 0; ti < tabs.length; ti++) {
            const tab = tabs[ti]
            const ci = (tab.children ?? []).findIndex((c) => c.id === layoutId && c.type === 'LayoutField')
            if (ci === -1) continue

            const layout = tab.children[ci]
            const slots = (layout.extraAttributes?.slots ?? []) as { id: string; label: string; children: FormElementInstance[] }[]
            const si = slots.findIndex((s) => s.id === slotId)
            if (si === -1) return prev
            const nextSlots = [...slots]
            nextSlots[si] = { ...slots[si], children: mutator(slots[si].children ?? []) }
            const updatedLayout = { ...layout, extraAttributes: { ...layout.extraAttributes, slots: nextSlots } }

            const newChildren = [...(tab.children ?? [])]
            newChildren[ci] = updatedLayout
            const newTabs = [...tabs]
            newTabs[ti] = { ...tab, children: newChildren }
            newElements[i] = { ...el, extraAttributes: { ...el.extraAttributes, tabs: newTabs } }
            return newElements
          }
        }
      }

      return prev
    })
  }

  return (
      <DesignerContext.Provider
          value={{
            elements,
            setElements,
            addElement,
            removeElement,
            customCss,
            setCustomCss,
            useFloatingMenu,
            setUseFloatingMenu,
            floatingMenuStyle,
            setFloatingMenuStyle,
            bounceButton,
            setBounceButton,
            guidedFlow,
            setGuidedFlow,
            resultPage,
            setResultPage,
            actionFolderIds,
            setActionFolderIds,
            selectedElement,
            setSelectedElement,
            updateElement,
            updateNestedLayoutSlot,

            // ✅ NEW
            insertTarget,
            setInsertTarget,

            linkedDocTemplatesPanelOpen,
            setLinkedDocTemplatesPanelOpen,
          }}
      >
        {children}
      </DesignerContext.Provider>
  )
}
